import { ArrowGo, Pack } from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { Label } from "@radix-ui/react-label"
import { Store } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import { businessPath, ordersPath } from "@/routes"

interface Product {
  id: number
  name?: string
  title?: string
}
interface CartItem {
  id: number
  quantity: number
  notes?: string
  product: Product
}

interface Business {
  id: number
  name?: string
  business_name?: string
  neighborhood?: { id: number; name: string }
}

interface OrderSummary {
  subtotal: number
  delivery_fee: number
  total: number
  items_count: number
}

interface DeliveryZone {
  id?: number
}

interface Props {
  business: Business
  cart_items: CartItem[]
  delivery_zone?: DeliveryZone
  delivery_available?: boolean
  order_summary: OrderSummary
  user_address?: { neighborhood?: string; city?: string }
}

const formatPrice = (n: number) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  }).format(n)

export default function OrdersNew({
  business,
  cart_items,
  order_summary,
  delivery_available,
  user_address,
}: Props) {
  const initialDeliveryType =
    delivery_available === false ? "pickup" : "delivery"
  const [deliveryType, setDeliveryType] = useState<"delivery" | "pickup">(
    initialDeliveryType,
  )

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const formData = new FormData(form)

    const payload = {
      business_id: business.id,
      order: {
        delivery_address: {
          line1: (formData.get("address_line1") as string | null) ?? "",
          details: (formData.get("address_details") as string | null) ?? "",
          floor: (formData.get("address_floor") as string | null) ?? "",
        },
        payment_method:
          (formData.get("payment_method") as string | null) ?? "cash",
        notes: (formData.get("notes") as string | null) ?? "",
      },
      delivery_type:
        (formData.get("delivery_type") as string | null) ?? deliveryType,
    }

    router.post(ordersPath(), payload)
  }

  return (
    <AppLayout>
      <Head title="Confirmar pedido" />

      <div className="mt-4 w-full max-w-4xl">
        <div className="text-primary flex flex-wrap items-center gap-2 px-4 text-[13px] font-medium tracking-[0.13px]">
          <button
            onClick={() => window.history.back()}
            className="hover:underline"
          >
            <div className="flex items-center gap-1">
              <ArrowGo className="text-primary size-4 rotate-180 fill-current" />
              Carrito
            </div>
          </button>
        </div>
        <CardTitle className="text-foreground flex items-center gap-3 px-4 text-2xl font-bold">
          <Pack className="fill-primary h-8 w-8" />
          Confirmar Pedido
        </CardTitle>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="mt-4 space-y-4">
              <div className="px-4">
                <CardContent className="space-y-4 px-0">
                  <div className="">
                    <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                      Tipo de entrega
                    </Label>
                    <Select
                      name="delivery_type"
                      value={deliveryType}
                      onValueChange={(val) =>
                        setDeliveryType(val as "delivery" | "pickup")
                      }
                    >
                      <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          value="delivery"
                          disabled={delivery_available === false}
                        >
                          Envío
                        </SelectItem>
                        <SelectItem value="pickup">Retiro en local</SelectItem>
                      </SelectContent>
                    </Select>
                    {delivery_available === false && (
                      <div className="text-muted-foreground text-sm">
                        Envío no disponible para tu barrio{" "}
                        {user_address?.neighborhood
                          ? ` ${user_address.neighborhood}`
                          : ""}
                        .
                      </div>
                    )}
                  </div>

                  {deliveryType === "delivery" && (
                    <>
                      <div className="">
                        <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                          Dirección
                        </Label>
                        <Input
                          className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                          name="address_line1"
                          placeholder="Calle y número"
                          required
                        />
                      </div>
                      <div className="">
                        <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                          Piso (opcional)
                        </Label>
                        <Input
                          className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                          name="address_floor"
                          placeholder="Ej: 3B"
                        />
                      </div>
                      <div className="">
                        <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                          Detalles (opcional)
                        </Label>
                        <Input
                          className="border-border focus:border-primary h-12 border-2 text-base transition-colors duration-200"
                          name="address_details"
                          placeholder="Puerta, referencias"
                        />
                      </div>
                    </>
                  )}

                  <div className="">
                    <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                      {" "}
                      Método de pago{" "}
                    </Label>
                    <Select
                      name="payment_method"
                      defaultValue="cash"
                      onValueChange={(val) => {
                        const input = document.querySelector<HTMLInputElement>(
                          "input[name=payment_method]",
                        )
                        if (input) input.value = val
                      }}
                    >
                      <SelectTrigger className="border-border focus:border-primary h-12 border-2 px-3 py-5">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Efectivo</SelectItem>
                        <SelectItem value="card">Tarjeta</SelectItem>
                        <SelectItem value="transfer">Transferencia</SelectItem>
                      </SelectContent>
                    </Select>
                    <input type="hidden" name="payment_method" value="cash" />
                  </div>

                  <div className="">
                    <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                      {" "}
                      Notas (opcional)
                    </Label>
                    <Textarea
                      className="border-border focus:border-primary resize-none border-2 text-base transition-colors duration-200"
                      name="notes"
                      placeholder="Indicaciones, preferencias…"
                    />
                  </div>
                </CardContent>
              </div>
            </div>

            <div className="bg-card h-fit space-y-4 p-4 md:sticky md:top-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Store className="h-4 w-4" />
                <Link
                  href={businessPath(business.id)}
                  className="hover:underline"
                >
                  {business.business_name ?? business.name}
                </Link>
              </CardTitle>
              <CardContent className="space-y-3">
                {cart_items.map((it) => (
                  <div
                    key={it.id}
                    className="flex items-start justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="truncate">
                        {it.product.name ?? it.product.title ?? "Producto"} x{" "}
                        {it.quantity}
                      </div>
                      {it.notes && (
                        <div className="text-muted-foreground truncate text-xs">
                          {it.notes}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <div className="space-y-1 border-t pt-2 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span className="font-medium">
                      {formatPrice(order_summary.subtotal)}
                    </span>
                  </div>
                  {order_summary.delivery_fee > 0 && (
                    <div className="flex justify-between">
                      <span>Envío</span>
                      <span className="font-medium">
                        {formatPrice(order_summary.delivery_fee)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-base">
                    <span>Total</span>
                    <span className="font-bold">
                      {formatPrice(order_summary.total)}
                    </span>
                  </div>
                </div>

                <Button type="submit" className="w-full">
                  Realizar pedido
                </Button>
              </CardContent>
            </div>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
